import { useCallback, useMemo, useState } from "react";

import { useConstants } from "shared/hooks/useConstants";
import { useLocale } from "shared/hooks/useLocale";
import { useTables } from "shared/hooks/useTables";
import {
    createTable,
    createTablesBatch,
    deleteTable,
    updateTable,
} from "services/tables/rest";
import { TableDto } from "services/tables/restModels";
import { useNotificationSnackbar } from "shared/ui";

import {
    TableFormValues,
    TablesBatchFormValues,
    TablesManagementState,
} from "features/TablesManagement/types";

const byPublicId = (left: TableDto, right: TableDto) => left.publicId - right.publicId;

const matchesSearch = (table: TableDto, query: string) => {
    if (!query) return true;

    const haystack = [
        String(table.publicId),
        ...Object.values(table.title),
        ...Object.values(table.description),
        table.externalId || "",
    ]
        .join(" ")
        .toLowerCase();

    return haystack.includes(query.trim().toLowerCase());
};

export const useTablesManagementState = (): TablesManagementState => {
    const locale = useLocale();

    const {
        tables,
        loading: tablesLoading,
        error: tablesError,
        setTables,
    } = useTables();
    const {
        constants,
        loading: constantsLoading,
        error: constantsError,
    } = useConstants();

    const notify = useNotificationSnackbar();

    const [search, setSearch] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);
    const [isTableDialogOpen, setIsTableDialogOpen] = useState<boolean>(false);
    const [isBatchDialogOpen, setIsBatchDialogOpen] = useState<boolean>(false);
    const [editedTable, setEditedTable] = useState<TableDto | null>(null);
    const [tableToDelete, setTableToDelete] = useState<TableDto | null>(null);

    // Основной язык кабинета — первым, чтобы переключатель открывался на нём.
    const availableLanguages = useMemo(() => {
        const locales = (constants?.locales || []).map((item) => item.id);
        return [locale, ...locales.filter((item) => item !== locale)];
    }, [constants, locale]);

    const visibleTables = useMemo(
        () => tables.filter((table) => matchesSearch(table, search)),
        [tables, search]
    );

    const nextPublicId = useMemo(
        () => tables.reduce((max, table) => Math.max(max, table.publicId), 0) + 1,
        [tables]
    );

    const occupiedNumbers = useMemo(() => tables.map((table) => table.publicId), [tables]);

    const openCreateDialog = useCallback(() => {
        setEditedTable(null);
        setIsTableDialogOpen(true);
    }, []);

    const openEditDialog = useCallback((table: TableDto) => {
        setEditedTable(table);
        setIsTableDialogOpen(true);
    }, []);

    const closeTableDialog = useCallback(() => {
        setIsTableDialogOpen(false);
        setEditedTable(null);
    }, []);

    const submitTable = useCallback(
        async (values: TableFormValues): Promise<boolean> => {
            setSaving(true);
            try {
                if (editedTable) {
                    const updated = await updateTable(editedTable.id, values);
                    if (updated) {
                        setTables(
                            tables
                                .map((table) => (table.id === updated.id ? updated : table))
                                .sort(byPublicId)
                        );
                    }
                    notify.showSuccess("Стол сохранён");
                } else {
                    const created = await createTable(values);
                    if (created) {
                        setTables([...tables, created].sort(byPublicId));
                    }
                    notify.showSuccess("Стол добавлен");
                }

                closeTableDialog();
                return true;
            } catch (e) {
                notify.showError("Не удалось сохранить стол", e);
                return false;
            } finally {
                setSaving(false);
            }
        },
        [closeTableDialog, editedTable, notify, setTables, tables]
    );

    const openBatchDialog = useCallback(() => setIsBatchDialogOpen(true), []);
    const closeBatchDialog = useCallback(() => setIsBatchDialogOpen(false), []);

    const submitBatch = useCallback(
        async (values: TablesBatchFormValues): Promise<boolean> => {
            setSaving(true);
            try {
                const result = await createTablesBatch(values);
                if (result) setTables(result);

                notify.showSuccess("Столы добавлены");
                closeBatchDialog();
                return true;
            } catch (e) {
                notify.showError("Не удалось добавить столы", e);
                return false;
            } finally {
                setSaving(false);
            }
        },
        [closeBatchDialog, notify, setTables]
    );

    const openDeleteDialog = useCallback((table: TableDto) => setTableToDelete(table), []);
    const closeDeleteDialog = useCallback(() => setTableToDelete(null), []);

    const confirmDelete = useCallback(async () => {
        if (!tableToDelete) return;

        setSaving(true);
        try {
            const result = await deleteTable(tableToDelete.id);
            if (result) setTables(result);

            notify.showSuccess("Стол удалён");
            setTableToDelete(null);
        } catch (e) {
            notify.showError("Не удалось удалить стол", e);
        } finally {
            setSaving(false);
        }
    }, [notify, setTables, tableToDelete]);

    return {
        tables: visibleTables,
        totalCount: tables.length,
        // Константы нужны для переключателя языков — гейтим страницу и на них.
        loading: tablesLoading || constantsLoading,
        error: tablesError || constantsError,
        saving,

        locale,
        availableLanguages,
        nextPublicId,
        occupiedNumbers,

        search,
        setSearch,

        editedTable,
        isTableDialogOpen,
        openCreateDialog,
        openEditDialog,
        closeTableDialog,
        submitTable,

        isBatchDialogOpen,
        openBatchDialog,
        closeBatchDialog,
        submitBatch,

        notify,

        tableToDelete,
        openDeleteDialog,
        closeDeleteDialog,
        confirmDelete,
    };
};
